import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useFeatures } from "@/context/FeaturesContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Ship, Plane, ArrowLeft } from "lucide-react";

function formatErr(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { t } = useI18n();
  const { isOn } = useFeatures();
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "customer", phone: "", country: "", city: "", company_name: "", receiving_address: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [fbEnabled, setFbEnabled] = useState(false);
  const [fbLinkPending, setFbLinkPending] = useState(!!sessionStorage.getItem("fb_link_ticket"));

  useEffect(() => {
    api.get("/auth/facebook/status").then(({ data }) => setFbEnabled(!!data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    const fbLink = searchParams.get("fb_link");
    const fbStatus = searchParams.get("facebook");
    if (fbLink) {
      sessionStorage.setItem("fb_link_ticket", fbLink);
      setFbLinkPending(true);
    }
    if (fbStatus === "cancelled") toast.info(t("fb.cancelled"));
    else if (fbStatus === "no_email") toast.error(t("fb.noEmail"));
    else if (fbStatus === "error") toast.error(t("fb.error"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const tryFbLink = async () => {
    const ticket = sessionStorage.getItem("fb_link_ticket");
    if (!ticket) return;
    try {
      await api.post("/auth/facebook/link", { ticket });
      toast.success(t("fb.linked"));
    } catch (e2) {
      toast.error(formatErr(e2.response?.data?.detail));
    } finally {
      sessionStorage.removeItem("fb_link_ticket");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (isLogin) {
        const u = await login(form.email, form.password);
        await tryFbLink();
        toast.success(t("auth.connected"));
        if (u && u.email_verified === false) { navigate("/verify-email"); return; }
        const saved = sessionStorage.getItem("redirect_after_login");
        if (saved) { sessionStorage.removeItem("redirect_after_login"); navigate(saved); return; }
        navigate("/dashboard");
      } else {
        await register(form);
        toast.success(t("auth.accountCreated"));
        navigate("/verify-email");
      }
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const facebookLogin = () => {
    window.location.href = `${API}/auth/facebook/start`;
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative bg-primary">
        <div className="absolute inset-0 opacity-30 bg-cover bg-center" style={{ backgroundImage: "url(https://images.unsplash.com/photo-1494412519320-aa613dfb7738?w=1000&q=80)" }} />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2 font-heading font-extrabold text-2xl"><span className="grid h-9 w-9 place-items-center bg-accent"><Ship className="h-5 w-5" /></span> Colis2Go</Link>
          <div>
            <h2 className="font-heading font-extrabold text-4xl tracking-tighter leading-tight">{t("hero.title")}</h2>
            <p className="mt-3 text-white/80 flex items-center gap-3"><Plane className="h-5 w-5 text-accent" /> <Ship className="h-5 w-5 text-accent" /> {t("app.tagline")}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-background">
        <div className="w-full max-w-sm">
          <div className="mb-6 flex items-center justify-between">
            <Link to="/" data-testid="auth-logo-mobile" className="md:hidden flex items-center gap-2 font-heading font-extrabold text-lg text-primary">
              <span className="grid h-7 w-7 place-items-center bg-accent text-white"><Ship className="h-4 w-4" /></span> Colis2Go
            </Link>
            <Link to="/" data-testid="back-home" className="text-sm text-muted-foreground hover:text-accent flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" /> {t("auth.backHome")}
            </Link>
          </div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">{isLogin ? t("auth.loginTitle") : t("auth.registerTitle")}</h1>

          {fbLinkPending && (
            <div data-testid="fb-link-banner" className="mt-4 border border-accent bg-accent/10 p-3 text-sm text-primary">
              {t("fb.linkPrompt")}
            </div>
          )}

          <div className="mt-6 space-y-3">
            {fbEnabled && (
              <Button data-testid="facebook-login" type="button" variant="outline" className="w-full h-11 rounded-sm font-semibold" onClick={facebookLogin}>
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.026 1.792-4.697 4.533-4.697 1.313 0 2.686.235 2.686.235v2.971h-1.513c-1.491 0-1.956.93-1.956 1.886v2.265h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>
                {t("auth.facebook")}
              </Button>
            )}
            <Button data-testid="google-login" type="button" variant="outline" className="w-full h-11 rounded-sm font-semibold" onClick={googleLogin}>
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/></svg>
              {t("auth.google")}
            </Button>
          </div>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground"><span className="flex-1 h-px bg-border" /> {t("auth.or")} <span className="flex-1 h-px bg-border" /></div>

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label>{t("auth.name")}</Label>
                <Input data-testid="auth-name" className="rounded-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </div>
            )}
            <div>
              <Label>{t("auth.email")}</Label>
              <Input data-testid="auth-email" type="email" className="rounded-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div>
              <Label>{t("auth.password")}</Label>
              <Input data-testid="auth-password" type="password" className="rounded-sm" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
              {isLogin ? (
                <div className="mt-1.5 text-right">
                  <Link data-testid="forgot-link" to="/forgot-password" className="text-xs text-accent font-semibold hover:underline">{t("auth.forgot")}</Link>
                </div>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">{t("auth.pwdMin")}</p>
              )}
            </div>
            {!isLogin && (
              <div>
                <Label>{t("auth.role")}</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {["customer", "partner"].filter((r) => isOn(r === "customer" ? "customer_registration" : "partner_registration")).map((r) => (
                    <button type="button" key={r} data-testid={`role-${r}`} onClick={() => setForm({ ...form, role: r })}
                      className={`h-11 border text-sm font-semibold transition-colors ${form.role === r ? "bg-primary text-white border-primary" : "bg-white border-border hover:border-primary"}`}>
                      {r === "customer" ? t("auth.customer") : t("auth.partner")}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {!isLogin && (
              <>
                <div>
                  <Label>{t("auth.phone")} <span className="text-xs text-muted-foreground font-normal">(facultatif)</span></Label>
                  <Input data-testid="auth-phone" type="tel" placeholder="+225 07 00 00 00 00" className="rounded-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                {form.role === "partner" && (
                  <div>
                    <Label>{t("auth.company")}</Label>
                    <Input data-testid="auth-company" className="rounded-sm" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} required />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{form.role === "partner" ? t("auth.depotCountry") : t("auth.country")}</Label>
                    <Input data-testid="auth-country" className="rounded-sm" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{form.role === "partner" ? t("auth.depotCity") : t("auth.city")}</Label>
                    <Input data-testid="auth-city" className="rounded-sm" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required />
                  </div>
                </div>
                {form.role === "partner" && (
                  <div>
                    <Label>{t("auth.address")}</Label>
                    <Input data-testid="auth-address" className="rounded-sm" value={form.receiving_address} onChange={(e) => setForm({ ...form, receiving_address: e.target.value })} />
                    <p className="mt-1 text-xs text-muted-foreground">{t("auth.addressHint")}</p>
                  </div>
                )}
              </>
            )}
            {err && <p data-testid="auth-error" className="text-sm text-destructive">{err}</p>}
            <Button data-testid="auth-submit" type="submit" disabled={busy} className="w-full h-11 rounded-sm bg-accent hover:bg-accent/90 text-white font-bold active:scale-95 transition-transform">
              {isLogin ? t("auth.loginBtn") : t("auth.registerBtn")}
            </Button>
          </form>

          <p className="mt-6 text-sm text-muted-foreground text-center">
            {isLogin ? t("auth.noAccount") : t("auth.hasAccount")}{" "}
            <Link data-testid="auth-switch" to={isLogin ? "/register" : "/login"} className="text-accent font-semibold hover:underline">
              {isLogin ? t("nav.register") : t("nav.login")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
